//  ------------------------------------------------------------------------------
//  INPUT FILE HANDLING CONSTANTS
//  ------------------------------------------------------------------------------


export const InputFileAcceptMimes = {
    images: 'image/*',
    videos: 'video/mp4,video/mov,video/x-m4v,video/*',
    audios: 'audio/*',
}

/**
 * Different types of file types the file-fetcher of browser accepts
 */
export const InputFileAcceptTypes = {
    // Images
    png: '.png' as const,
    jpg: '.jpg' as const,
    jpeg: '.jpeg' as const,
    gif: '.gif' as const,
    bmp: '.bmp' as const,
    tiff: '.tiff' as const,
    apng: '.apng' as const,
    pjpeg: '.pjpeg' as const,
    tif: '.tif' as const,
    svg: '.svg' as const,
    webp: '.webp' as const,
    heic: '.heic' as const,
    heif: '.heif' as const,
    ico: '.ico' as const,
    avif: '.avif' as const,

    // Videos
    mp4: '.mp4' as const,
    mov: '.mov' as const,
    webm: '.webm' as const,
    avi: '.avi' as const,
    mkv: '.mkv' as const,
    flv: '.flv' as const,
    wmv: '.wmv' as const,
    mpeg: '.mpeg' as const,
    mpg: '.mpg' as const,
    ogv: '.ogv' as const,
    hevc: '.hevc' as const,

    // Audio
    mp3: '.mp3' as const,
    wav: '.wav' as const,
    m4a: '.m4a' as const,
    ogg: '.ogg' as const,
    flac: '.flac' as const,
    aac: '.aac' as const,
    wma: '.wma' as const,

    // Documents
    pdf: '.pdf' as const,
    txt: '.txt' as const,
    rtf: '.rtf' as const,
    doc: '.doc' as const,
    docx: '.docx' as const,
    odt: '.odt' as const,
    markdown: '.md' as const,

    // Presentations
    ppt: '.ppt' as const,
    pptx: '.pptx' as const,
    key: '.key' as const,
    odp: '.odp' as const,

    // Spreadsheets
    csv: '.csv' as const,
    xls: '.xls' as const,
    xlsx: '.xlsx' as const,
    numbers: '.numbers' as const,
    ods: '.ods' as const,

    // Archives
    zip: '.zip' as const,
    rar: '.rar' as const,
    '7z': '.7z' as const,
    tar: '.tar' as const,
    gz: '.gz' as const,
    bz2: '.bz2' as const,

    // Code / Web Files
    html: '.html' as const,
    htm: '.htm' as const,
    css: '.css' as const,
    js: '.js' as const,
    jsx: '.jsx' as const,
    ts: '.ts' as const,
    tsx: '.tsx' as const,
    json: '.json' as const,
    xml: '.xml' as const,
    yml: '.yml' as const,
    yaml: '.yaml' as const,
    php: '.php' as const,
    py: '.py' as const,
    java: '.java' as const,
    c: '.c' as const,
    cpp: '.cpp' as const,
    cs: '.cs' as const,
    sh: '.sh' as const,
    bat: '.bat' as const,
    sql: '.sql' as const,

    // Design / 3D
    psd: '.psd' as const,
    ai: '.ai' as const,
    eps: '.eps' as const,
    fig: '.fig' as const,
    sketch: '.sketch' as const,
    fbx: '.fbx' as const,
    obj: '.obj' as const,
    stl: '.stl' as const,
    gltf: '.gltf' as const,
    glb: '.glb' as const,

    // Fonts
    ttf: '.ttf' as const,
    otf: '.otf' as const,
    woff: '.woff' as const,
    woff2: '.woff2' as const,

    // Miscellaneous
    log: '.log' as const,
    ini: '.ini' as const,
    yaml_config: '.yaml' as const,
    json_config: '.json' as const,
    bin: '.bin' as const,
    exe: '.exe' as const,
    dmg: '.dmg' as const,
    iso: '.iso' as const,
};


//  ------------------------------------------------------------------------------
//  APPLICATION CONSTANTS
//  ------------------------------------------------------------------------------

/**
 * The different types of product technical property types.
 */
export const ProductTechnicalPropertyTypeIds = {
    // Text, uses a text input field (autocomplete)
    text: 1,
    // Number, uses a number input field
    number: 2,
    // Date, uses a date picker
    date: 3,
    // Boolean, uses a checkbox
    boolean: 4,
    // Decimal, uses a decimal input field
    decimal: 5,
    // Currency, uses a currency input field
    money: 6,
    // This is a special type used to render values specific to feet and inches.
    fraction: 7,
    // This is for colors. For colors we always choose to just show a colored-ball without any text value.
    color: 8,
}

/**
 * The different types of product technical property leaf types.
 */
export const ProductPropertyGroupRecordLeafTypes = {
    // The range means that the value is a range of values. used to determine how the cell is rendered and how its value upsertion is handled.
    range: 1,
    // The default means that the value is a single value. used to determine how the cell is rendered and how its value upsertion is handled.
    default: 2,
}


/**
 * Types that are supported in the technical files section of the application.
 */
export const ProductTechnicalFileTypes = {
    unknown: 1 as const,
    PDF: 2 as const,
    HTML: 4 as const,
    IMAGE: 5 as const,
}


/**
 * The different available product types in the system.
 */
export const ProductTypes = {
    normal: 'N',
    nonPhysical: 'V',
    manufactured: 'M',
    kitted: 'K',
    rawMaterial: 'R',
    macro: 'C',
}

/**
 * Product delivery mode options.
 */
export const DeliveryModeConst = {
    PickupAndDelivery: 0,
    PickupOnly: 1,
    DeliveryOnly: 2,
} as const;

export type DeliveryMode = typeof DeliveryModeConst[keyof typeof DeliveryModeConst];

export const isValidDeliveryMode = (value: unknown): value is DeliveryMode =>
    value === DeliveryModeConst.PickupAndDelivery
    || value === DeliveryModeConst.PickupOnly
    || value === DeliveryModeConst.DeliveryOnly;

/**
 * The name of the sections that are marked for caching in the product details view.
 */
export const MarkProductForCacheSectionNames = {
    tag: 'tag',
    shareStatus: "shareStatus",
    visibility: "visibility",
    upc: 'upc',
    cost: 'cost',
    category: 'category',
    description: 'description',
    code: 'code',
    image: 'image',
    propertyGroup: 'propertyGroup',
    relations: 'relations',
    technical: 'technical',
    variants: 'variants',
}


/**
 * The different types of the nexus search schemas.
 */
export const NexusSearchSchemaTypes = {
    field: 1,
    propertyGroup: 2,
    property: 3,
}

/**
 * The static names of the industry schemas that enable us to create the filters for search view from other views.
 */
export const NexusSearchSchemaEntryNames = {
    parentCategory: "ParentCategory",
    category: "Category",
    partNo: "PartNumber",
    tag: "FlatTags",
    code: "Codes",
    variety: "Variety",
    submitDateTime: "SubmitDateTime",
}

/**
 * The static names of the industry schemas that enable us to create the filters for search view from other views.
 */
export const PlatformIndustryIds = {
    hdAutoPart: 'a95754d0-a600-4f1c-a775-0134cb2c4663',
    autoPart: 'b4ecc9cc-37e2-4795-9ee6-3abb21a2694f',
    machinery: '176ebdfc-c2ba-462c-ba1a-99f3e04ff49e',
    westernApparel: '68131929-05f8-417b-868a-b1cee3262ce8',
    boilerControls: '00d5477f-2d32-43a2-b790-1441297ce16d',
}


/**
 * The different types of product technical property leaf types.
 * used in the UI. Directly mapped to the [CustomerPaymentMethodTypeNames]
 */
export const CustomerPaymentMethodTypes = {
    masterCard: 'M' as const,
    mastercard: 'M' as const,
    visa: 'V' as const,
    americanExpress: 'AX' as const,
    americanexpress: 'AX' as const,
    americanExpress2: 'A' as const,
    novusDiscover: 'NO' as const,
    discover: 'NO' as const,
    discover2: 'discover' as const,
    debit: 'D' as const,
    jcb: 'C1' as const,
    jcb2: 'jcb' as const,
    sears: 'SE' as const,
}

/**
 * The different types of customer payment method names.
 * used in the UI. Directly mapped to the [CustomerPaymentMethodTypes]
 */
export const CustomerPaymentMethodTypeNames = {
    M: 'masterCard' as const,
    V: 'visa' as const,
    AX: 'amex' as const,
    A: 'amex' as const,
    NO: 'discover' as const,
    discover: 'discover' as const,
    D: 'debit' as const,
    C1: 'jcb' as const,
    jcb: 'jcb' as const,
    SE: 'sears' as const,
}


/**
 * The ids of the different types of credit card providers.
 */
export const CreditCardProviders = {
    moneris: 'Moneris',
    stripe: 'Stripe',
}

/**
 * The different types of customer addresses.
 */
export const CustomerAddressTypes = {
    // The shipping address of the customer.
    shipping: 1,
    // The billing address of the customer.
    billing: 2,
}

/**
 * Different types of customer membership names.
 */
export const CustomerMemberShipNames = {
    // user is onhold. used to determine action when in members list view.
    onHold: 'OnHold',
    // user is active. used to determine action when in members list view.
    onHoldCompanyLost: 'OnHoldCompanyLost',
    // user is active. used to determine action when in members list view.
    active: 'Active',
    // user is inactive. used to determine action when in members list view.
    deactivated: 'Deactivated',
}

/**
 * Gift card types that are supported in the application.
 */
export const CustomerGiftCardTypeIds = {
    refillable: 2,
    giftCard: 1,
    coupon: 3,
}

/**
 * Static payment terms that are used in the logic of the application
 */
export const CustomerPaymentTerms = {
    COD: 'COD',
}

/**
 * The different types of warranty and return message types.
 */
export const WarrantyAndReturnMessageTypes = {
    text: 1,
    image: 2,
    forwardedMessage: 3,
    pdf: 4,
    video: 5,
    zip: 6,
    // time should always be the biggest number since the sorting algorithm depends on this condition
    time: 1000,
}

/**
 * The different types of warranty and return message senders.
 */
export const WarrantyAndReturnMessageSenderTypes = {
    user: 3,
    operator: 2,
    system: 1,
}

/**
 * The different types of warranty and return message statuses.
 */
export const WarrantyAndReturnRequestStatuses = {
    initial: 1,
    waitForOperator: 2,
}

/**
 * Different types of warranty and return requests.
 */
export const WarrantyAndReturnTypes = {
    warranty: 1,
    return: 2,
}

/**
 * The different types of warranty and return message statuses.
 */
export const AccountingTransactionTypes = {
    invoice_ui: '__Invoice__', // this one is ui only
    giftCard: 'GiftCard',
    userOrder: 'UserOrder',
    invoice: 'Invoice',
    CreditMemo: 'CreditMemo',
    payment: 'OnlinePayment',
    otherPayment: 'OtherPayments',
}

/**
 * The different payment options that are available in the accounting
 */
export const AccountingPaymentOptions = {
    otherPayments: 'chequeOrCash',
    onlinePayment: 'card',
}


/**
 * The different platforms of the homepage.
 */
export const HomepagePlatforms = {
    web: 1 as const,
    phone: 2 as const,
}

/**
 * The Available notification channel types.
 */
export const NotificationChannelTypes = {
    user: 1,
    userAndSystem: 2,
    system: 3,
    operator: 4,
    operatorAndSystem: 5,
}

/**
 * The types of the vendor notification types.
 */
export const VendorProfileNotificationTypes = {
    warning: 1,
    error: 1,
    info: 1,
}


/**
 * The types of task statuses.
 */
export enum TaskStatusTypes {
    ToDo = "ToDo",
    InProgress = "InProgress",
    Done = "Done",
}

/**
 * The different types of product technical property leaf types.
 */
export const UploadStates = {
    // Error state
    error: 'error',
    // Loading state
    pending: 'pending',
    // Success state
    done: 'done',
}

/**
 * The default page sizes of the table pagination
 */
export const DefaultPageSizes = [
    10 as const,
    20 as const,
    50 as const,
    100 as const,
];

/**
 * The default pagination info of the application
 */
export const DefaultPaginationInfo = {
    pageSize: DefaultPageSizes[0],
    length: 0 as const,
    currentPage: 1 as const,
    sizes: DefaultPageSizes,
}

/**
 * The default pagination info of the application
 */
export const DefaultInitialPaginationInfo = {
    ...DefaultPaginationInfo,
    currentPage: -1 as const,
}

/**
 * Width to height to an aspect ratio for the banners
 */
export const bannerWidthToHeightRatio = 2.32 as const;

/**
 * Scope of a partner configuration property (List.typeId).
 */
export const ConfigurationTypes = {
    organization: 1,
    personal: 2,
    both: 3,
} as const;

/**
 * Dynamic data type of a partner configuration property (List.dynamicDataTypeId).
 */
export const ConfigurationDataTypes = {
    list: 1,
    number: 2,
    string: 3,
    boolean: 4,
} as const;

/**
 * Freight estimation provider options for organizational settings.
 */
export enum FreightProvider {
    Local = 'Local',
    FreightCOM = 'Freightcom',
}

/**
 * Shipping bill-to options for freight info on quotes, sales orders, invoices, and credits.
 */
export const ShippingBillTo = {
    prepaid: 'PREPAID',
    collect: 'COLLECT',
    thirdParty: 'THIRD_PARTY',
    myAccount: 'MY_ACCOUNT',
} as const;

export const ShippingBillToList = [
    ShippingBillTo.prepaid,
    ShippingBillTo.collect,
    ShippingBillTo.thirdParty,
    ShippingBillTo.myAccount,
] as const;

/**
 * Inventory transfer status values returned by the API (`transferStatusName`).
 */
export const InventoryTransferStatuses = {
    pending: 'Pending',
    scanning: 'Scanning',
    inTransit: 'InTransit',
    arrived: 'Arrived',
    finalized: 'Finalized',
    posted: 'Posted',
} as const;

export const InventoryTransferStatusList = [
    InventoryTransferStatuses.pending,
    InventoryTransferStatuses.scanning,
    InventoryTransferStatuses.inTransit,
    InventoryTransferStatuses.arrived,
    InventoryTransferStatuses.finalized,
    InventoryTransferStatuses.posted,
] as const;

export type InventoryTransferStatus = typeof InventoryTransferStatuses[keyof typeof InventoryTransferStatuses];

/**
 * The access level tokens of the users that would determine in which parts of the system the user can access.
 */
export const CustomerMemberAccessLevels = {
    purchase: '12255197-479E-4F92-B1B8-980167E416F3' as const,
    payment: 'D59004BD-33F9-4804-B080-F90478206A36' as const,
    lookup: '95A2F1EF-559D-4695-8491-F64BA5B5652B' as const,
    warrantyAndReturn: '060AD386-A7C3-48F9-8F6A-7D5978A84594' as const,
}